import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { rotuloEtapa } from "@shared/etapas";

// ─── Radar de card parado (SLA) ────────────────────────────────────────────────
// Raz-only (gate na rota). Lista cards de todos os clientes com semáforo por
// tempo na coluna. Clicar abre o card via /c/:key. Pedido João ago/26.

const REDE: Record<string, string> = {
  instagram: "Instagram", linkedin: "LinkedIn", tiktok: "TikTok",
  youtube: "YouTube", twitter: "Twitter/X",
};
const SEMAFORO: Record<string, { cor: string; label: string }> = {
  preto: { cor: "#1c1c1c", label: "Estourado 3+ dias" },
  vermelho: { cor: "#DC2626", label: "Estourado" },
  amarelo: { cor: "#D97706", label: "Perto do limite" },
  // SLA em dia, mas o card está numa etapa que o quadro do cliente não usa.
  ok: { cor: "#B42318", label: "Fora do quadro" },
};

function fmtData(ms: number | null): string {
  if (ms == null) return "—";
  const d = new Date(ms + 3 * 3600000); // BRT
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function Radar() {
  const [, navigate] = useLocation();
  const { data: rows = [], isLoading } = trpc.radar.list.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const { data: sla = [] } = trpc.radar.slaConfig.useQuery();

  const [fCliente, setFCliente] = useState("todos");
  const [fResp, setFResp] = useState("todos");
  const [fStatus, setFStatus] = useState("todos");

  const clientes = useMemo(
    () => [...new Set(rows.map(r => r.clientNome).filter(Boolean))].sort(),
    [rows]
  );
  const responsaveis = useMemo(
    () => [...new Set(rows.map(r => r.responsavelNome).filter(Boolean) as string[])].sort(),
    [rows]
  );

  const filtradas = rows.filter(r =>
    (fCliente === "todos" || r.clientNome === fCliente) &&
    (fResp === "todos" || r.responsavelNome === fResp) &&
    (fStatus === "todos" || r.status === fStatus)
  );

  const nPreto = rows.filter(r => r.status === "preto").length;
  const nVermelho = rows.filter(r => r.status === "vermelho").length;
  const nAmarelo = rows.filter(r => r.status === "amarelo").length;

  const abrir = (r: { contentId: string | null; id: number }) =>
    navigate(`/c/${r.contentId || r.id}`);

  return (
    <div className="h-full overflow-y-auto bg-[var(--solar-bg)]">
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-baseline gap-3 mb-1">
          <h1 className="text-[var(--solar-ink)] font-bold text-2xl" style={{ fontFamily: "Poppins, sans-serif" }}>
            Radar
          </h1>
          <span className="text-[var(--solar-mute)] text-sm">cards parados por SLA de coluna · todos os clientes</span>
        </div>
        <p className="text-[var(--solar-mute)] text-[13px] mb-5">
          O relógio zera a cada troca de coluna. Dias úteis (fim de semana não conta). Só Raz vê esta tela.
        </p>

        {/* Cards de resumo */}
        <div className="grid grid-cols-3 gap-3 mb-5 max-w-xl">
          <Resumo n={nPreto} label="Estourados 3+ dias" cor="#1c1c1c" />
          <Resumo n={nVermelho} label="Estourados" cor="#DC2626" />
          <Resumo n={nAmarelo} label="Perto do limite" cor="#D97706" />
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Select value={fCliente} onChange={setFCliente} opts={["todos", ...clientes]} labelAll="Todos os clientes" />
          <Select value={fResp} onChange={setFResp} opts={["todos", ...responsaveis]} labelAll="Todos os responsáveis" />
          <Select value={fStatus} onChange={setFStatus}
            opts={["todos", "preto", "vermelho", "amarelo"]}
            labelMap={{ todos: "Todos os status", preto: "⚫ 3+ dias", vermelho: "🔴 Estourado", amarelo: "🟡 Perto" }} />
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[var(--solar-mute)] border-b border-[var(--solar-line)]" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  <th className="py-2.5 pl-4 pr-2 font-semibold"></th>
                  <th className="py-2.5 px-2 font-semibold">Cliente</th>
                  <th className="py-2.5 px-2 font-semibold">Título</th>
                  <th className="py-2.5 px-2 font-semibold">Rede</th>
                  <th className="py-2.5 px-2 font-semibold">Coluna</th>
                  <th className="py-2.5 px-2 font-semibold text-right">Dias na col.</th>
                  <th className="py-2.5 px-2 font-semibold">Bola</th>
                  <th className="py-2.5 px-2 font-semibold">Responsável</th>
                  <th className="py-2.5 px-2 font-semibold">Prevista</th>
                  <th className="py-2.5 px-2 font-semibold text-right">Dias p/ data</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={10} className="py-8 text-center text-[var(--solar-mute)]">Carregando…</td></tr>
                )}
                {!isLoading && filtradas.length === 0 && (
                  <tr><td colSpan={10} className="py-8 text-center text-[var(--solar-mute)]">Nenhum card em alerta. 🎉</td></tr>
                )}
                {filtradas.map(r => {
                  const s = SEMAFORO[r.status];
                  return (
                    <tr key={`${r.clientId}-${r.id}`}
                      onClick={() => abrir(r)}
                      className="border-b border-[var(--solar-line)] hover:bg-[var(--solar-bg)] cursor-pointer">
                      <td className="py-2.5 pl-4 pr-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: s?.cor }} title={s?.label} />
                      </td>
                      <td className="py-2.5 px-2 text-[var(--ink-2)] whitespace-nowrap">{r.clientNome}</td>
                      <td className="py-2.5 px-2 text-[var(--solar-ink)] font-medium max-w-[320px] truncate">
                        {r.titulo}
                        {r.contentId && <span className="ml-2 text-[11px] text-[var(--solar-mute)] font-mono">{r.contentId}</span>}
                      </td>
                      <td className="py-2.5 px-2 text-[var(--solar-mute)] whitespace-nowrap">{r.rede ? (REDE[r.rede] ?? r.rede) : "—"}</td>
                      <td className="py-2.5 px-2 text-[var(--ink-2)] whitespace-nowrap">{(r as any).colunaLabel ?? r.coluna}{(r as any).foraDoQuadro ? " (fora do quadro)" : ""}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums font-semibold" style={{ color: s?.cor }}>
                        {r.businessDays}{r.sla != null && <span className="text-[var(--solar-mute)] font-normal">/{r.sla}</span>}
                      </td>
                      <td className="py-2.5 px-2">
                        {r.dono && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium"
                            style={r.dono === "raz"
                              ? { background: "var(--brand-soft)", color: "var(--brand-ink)" }
                              : { background: "#FEF3C7", color: "#92400E" }}>
                            {r.dono === "raz" ? "Raz" : "Cliente"}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-[var(--solar-mute)] whitespace-nowrap">{r.responsavelNome ?? "—"}</td>
                      <td className="py-2.5 px-2 text-[var(--solar-mute)] tabular-nums whitespace-nowrap">{fmtData(r.dataPrevistaMs)}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums whitespace-nowrap"
                        style={{ color: r.diasAteData != null && r.diasAteData <= 3 ? "#DC2626" : "var(--solar-mute)" }}>
                        {r.diasAteData == null ? "—" : r.diasAteData}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legenda SLA */}
        {sla.length > 0 && (
          <p className="text-[11px] text-[var(--solar-mute)] mt-3">
            SLA por coluna (dias úteis):{" "}
            {sla.map(c => `${rotuloEtapa(c.coluna, [])} ${c.slaDias}d (${c.dono})`).join(" · ")}
            {" · "}agendado com data passada = alerta imediato.
          </p>
        )}
      </div>
    </div>
  );
}

function Resumo({ n, label, cor }: { n: number; label: string; cor: string }) {
  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: cor }} />
        <span className="text-2xl font-bold tabular-nums" style={{ color: cor, fontFamily: "Poppins, sans-serif" }}>{n}</span>
      </div>
      <div className="text-[11px] text-[var(--solar-mute)] mt-0.5">{label}</div>
    </div>
  );
}

function Select({ value, onChange, opts, labelAll, labelMap }: {
  value: string; onChange: (v: string) => void; opts: string[];
  labelAll?: string; labelMap?: Record<string, string>;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="text-[13px] bg-white border border-[var(--solar-line)] rounded-lg px-3 py-1.5 text-[var(--ink-2)] focus:outline-none focus:border-[var(--brand)]">
      {opts.map(o => (
        <option key={o} value={o}>
          {o === "todos" && labelAll ? labelAll : (labelMap?.[o] ?? o)}
        </option>
      ))}
    </select>
  );
}
